import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getCheckoutStatus } from "../lib/api";
import { useAccess } from "../lib/access";
import { useCart } from "../lib/cart";
import { trackPurchase } from "../lib/analytics";
import { CheckCircle2, Loader2, XCircle, Home, Wand2, Calendar, FileDown, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import useSeo from "../lib/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const { grant, entitlements } = useAccess();
  const { items: cartItems, clearCart } = useCart();

  useSeo({
    title: "Order confirmed | Plate Buddies",
    description: "Thanks for your Plate Buddies order — your adventure is on its way.",
    url: "https://platebuddies.com/checkout/success",
    noIndex: true,
  });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          // Fire GA4 purchase (idempotent — trackOnce dedupes on re-poll).
          try {
            trackPurchase({
              transaction_id: sessionId,
              value: (data.amount_total || 0) / 100,
              currency: (data.currency || "usd").toUpperCase(),
              items: cartItems,
              coupon: data.metadata?.coupon,
            });
          } catch (err) {
            console.warn("[analytics] purchase event failed:", err);
          }
          // Clear the local cart on successful payment so returning shoppers
          // don't see the just-paid items still lingering.
          try {
            if (cartItems && cartItems.length) clearCart();
          } catch {
            /* non-blocking */
          }
          // Auto-grant entitlements from the order.
          if (data.entitlements && (data.entitlements.planner || data.entitlements.generator)) {
            grant({ email: data.email, entitlements: data.entitlements });
          }
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        attempts += 1;
        if (attempts >= 10) {
          setState({ status: "timeout", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= 10) {
          setState({ status: "error", data: null });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const dp = state.data?.digital_print;
  const isDigitalPrint = !!dp?.is_digital_print;
  const isColoringPage = !!dp?.is_coloring_page;
  const digitalItems = Array.isArray(dp?.items) ? dp.items : [];
  const isMultiDigital = isDigitalPrint && digitalItems.length > 1;
  const downloadHref = dp?.download_url ? `${BACKEND_URL}${dp.download_url}` : null;
  // Show the recovery form on any non-paid terminal state. The most common
  // cause is the dashboard webhook not being wired, so polling exhausts.
  const showRecovery = ["timeout", "expired", "error"].includes(state.status);

  return (
    <div data-testid="page-checkout-success" className="min-h-screen bg-pb-cream flex items-center justify-center p-6 font-body">
      <div className="max-w-xl w-full bg-white rounded-[2.5rem] shadow-[0_30px_80px_rgba(242,106,52,0.15)] p-10 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 text-pb-coral animate-spin" />
            <h1 className="font-display text-3xl mt-6 font-black">Checking your order…</h1>
            <p className="text-pb-soft mt-2">Hang tight, we're confirming your adventure pack.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-pb-green" />
            <h1 data-testid="text-checkout-success" className="font-display text-4xl mt-6 font-black">
              {isMultiDigital
                ? `Your ${digitalItems.length} PDFs are ready! 🎨`
                : isColoringPage
                ? "Grab the crayons! 🖍️"
                : isDigitalPrint
                ? "Your Buddy is ready! 🎨"
                : "You're in the family!"}
            </h1>
            <p className="text-pb-soft mt-3">
              {isMultiDigital
                ? `Tap each button below to download your PDFs. A copy of every file was also emailed${state.data.email ? ` to ${state.data.email}` : ""}.`
                : isColoringPage
                ? `Tap below to download ${dp?.buddy_name || "your Buddy"}'s coloring page. A copy was also emailed${state.data.email ? ` to ${state.data.email}` : ""}.`
                : isDigitalPrint
                ? `Tap below to download ${dp?.buddy_name || "your Buddy"}'s print bundle. A copy was also emailed${state.data.email ? ` to ${state.data.email}` : ""}.`
                : "Your Plate Buddies adventure is on its way. Get ready for happier dinners."}
            </p>
            <div className="mt-4 text-sm text-pb-soft">
              Amount: ${state.data.amount_total ? (state.data.amount_total / 100).toFixed(2) : "—"} {state.data.currency?.toUpperCase()}
            </div>

            {isMultiDigital && (
              <div
                data-testid="digital-prints-download-list"
                className="mt-7 pt-6 border-t border-pb-sky/30 space-y-3 text-left"
              >
                {digitalItems.map((item) => {
                  const href = item.download_url ? `${BACKEND_URL}${item.download_url}` : null;
                  const label = item.is_coloring_page
                    ? "Coloring page"
                    : "Buddy + recipe";
                  return (
                    <a
                      key={item.index}
                      href={href || "#"}
                      data-testid={`btn-download-item-${item.index}`}
                      className="flex items-center justify-between gap-3 rounded-2xl bg-pb-cream hover:bg-white border-2 border-pb-sky/40 hover:border-pb-coral px-5 py-4 transition group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-pb-coral text-white flex items-center justify-center flex-shrink-0">
                          <FileDown className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-display font-black text-pb-ink text-base leading-tight truncate">
                            {item.buddy_name || "Your Buddy"}
                          </p>
                          <p className="text-[11px] font-extrabold uppercase tracking-widest text-pb-coral mt-0.5">
                            {label}
                          </p>
                        </div>
                      </div>
                      <span className="text-pb-coral font-extrabold group-hover:translate-x-1 transition text-sm">
                        Download →
                      </span>
                    </a>
                  );
                })}
                <p className="text-xs text-pb-soft inline-flex items-center gap-1.5 justify-center w-full">
                  <Mail className="h-3.5 w-3.5" />
                  We also emailed all {digitalItems.length} PDFs — check your inbox.
                </p>
                <SocialShare buddyName={dp?.buddy_name} />
              </div>
            )}

            {!isMultiDigital && isDigitalPrint && downloadHref && (
              <div data-testid="digital-print-download" className="mt-7 pt-6 border-t border-pb-sky/30">
                <a
                  href={downloadHref}
                  data-testid="btn-download-digital-print"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 hover:scale-105 transition shadow-[0_14px_32px_rgba(242,106,52,0.4)]"
                >
                  <FileDown className="h-5 w-5" />
                  {isColoringPage ? "Download my coloring page" : "Download my Buddy PDF"}
                </a>
                <p className="mt-3 text-xs text-pb-soft inline-flex items-center justify-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  We also emailed it — check your inbox in a minute or two.
                </p>
                <SocialShare buddyName={dp?.buddy_name} />
              </div>
            )}

            {!isDigitalPrint && (entitlements?.planner || entitlements?.generator) && (
              <div className="mt-7 pt-6 border-t border-pb-sky/30">
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral mb-3">
                  Unlocked instantly · saved to this browser
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  {entitlements?.planner && (
                    <Link
                      to="/planner"
                      data-testid="btn-unlocked-planner"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-5 py-3 hover:bg-pb-coral transition"
                    >
                      <Calendar className="h-4 w-4" /> Open Meal Planner
                    </Link>
                  )}
                  {entitlements?.generator && (
                    <Link
                      to="/create"
                      data-testid="btn-unlocked-generator"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-5 py-3 hover:scale-105 transition shadow-[0_8px_20px_rgba(242,106,52,0.35)]"
                    >
                      <Wand2 className="h-4 w-4" /> Make a Buddy
                    </Link>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-pb-coral" />
            <h1 className="font-display text-3xl mt-6 font-black">Almost there…</h1>
            <p className="text-pb-soft mt-2">
              Stripe is still confirming your payment. If you were charged, use the recovery box below — we'll re-deliver your file in under a minute.
            </p>
          </>
        )}

        {showRecovery && <RecoveryBlock sessionId={sessionId} />}

        <Link
          to={isDigitalPrint ? "/create" : "/"}
          data-testid="btn-back-home"
          className="inline-flex items-center gap-2 mt-8 rounded-full bg-pb-coral text-white font-bold px-7 py-3 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-105 transition"
        >
          <Home className="h-4 w-4" /> {isDigitalPrint ? "Design another Buddy" : "Back to Plate Buddies"}
        </Link>
      </div>
    </div>
  );
}

function SocialShare({ buddyName }) {
  return (
    <div data-testid="social-share-success" className="mt-6 pt-5 border-t border-dashed border-pb-sky/40">
      <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral mb-2">
        Show off {buddyName ? buddyName : "your Buddy"} ✨
      </p>
      <p className="text-xs text-pb-soft mb-3">
        Tag <span className="font-bold text-pb-ink">@plate_buddies</span> — we love reposting kid creations.
      </p>
      <div className="flex gap-2 justify-center flex-wrap">
        <a
          href="https://www.instagram.com/plate_buddies"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="social-instagram"
          className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-pb-sky/40 hover:border-pb-coral text-pb-ink font-extrabold px-4 py-2 text-xs transition"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="M12 2.16c3.2 0 3.58 0 4.85.07 1.17.05 1.8.25 2.23.42.56.21.96.47 1.38.89.42.42.68.82.89 1.38.16.42.36 1.05.42 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.42 2.23-.21.56-.47.96-.89 1.38-.42.42-.82.68-1.38.89-.42.16-1.05.36-2.23.42-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.42-.56-.21-.96-.47-1.38-.89-.42-.42-.68-.82-.89-1.38-.16-.42-.36-1.05-.42-2.23C2.16 15.58 2.16 15.2 2.16 12s0-3.58.07-4.85c.05-1.17.25-1.8.42-2.23.21-.56.47-.96.89-1.38.42-.42.82-.68 1.38-.89.42-.16 1.05-.36 2.23-.42C8.42 2.16 8.8 2.16 12 2.16M12 0C8.74 0 8.33 0 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.39-.67.67-1.08 1.34-1.39 2.13C.33 4.9.13 5.78.07 7.05 0 8.33 0 8.74 0 12s0 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.39 2.13.67.67 1.34 1.08 2.13 1.39.76.3 1.64.5 2.91.56C8.33 24 8.74 24 12 24s3.67 0 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.39.67-.67 1.08-1.34 1.39-2.13.3-.76.5-1.64.56-2.91C24 15.67 24 15.26 24 12s0-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.39-2.13C21.31 1.34 20.64.93 19.86.63 19.1.33 18.22.13 16.95.07 15.67 0 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 12 8a4 4 0 0 1 0 8zm6.41-10.4a1.44 1.44 0 1 1-1.44-1.44 1.44 1.44 0 0 1 1.44 1.44z" />
          </svg>
          Instagram
        </a>
        <a
          href="https://www.tiktok.com/@platebuddies"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="social-tiktok"
          className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-pb-sky/40 hover:border-pb-coral text-pb-ink font-extrabold px-4 py-2 text-xs transition"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.84 20.1a6.34 6.34 0 0 0 10.86-4.43V8.99a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.88-.42z" />
          </svg>
          TikTok
        </a>
        <a
          href="https://www.facebook.com/platebuddies"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="social-facebook"
          className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-pb-sky/40 hover:border-pb-coral text-pb-ink font-extrabold px-4 py-2 text-xs transition"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
          </svg>
          Facebook
        </a>
      </div>
    </div>
  );
}

function RecoveryBlock({ sessionId }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const recover = async (payload) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/digital-print/recover", payload);
      setResult(data);
      toast.success("Your PDF is on the way!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't recover that order. Email hello@platebuddies.com and we'll fix it.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const downloadHref = result.download_url ? `${BACKEND_URL}${result.download_url}` : null;
    return (
      <div data-testid="recovery-success" className="mt-6 pt-6 border-t border-pb-sky/30">
        <p className="text-sm text-pb-ink">
          🎉 {result.message}
        </p>
        {downloadHref && (
          <a
            href={downloadHref}
            data-testid="btn-recovery-download"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-7 py-4 hover:scale-105 transition shadow-[0_14px_32px_rgba(242,106,52,0.4)]"
          >
            <FileDown className="h-5 w-5" /> Download my PDF now
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="mt-7 pt-6 border-t border-pb-sky/30 text-left" data-testid="recovery-block">
      <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral mb-2 text-center">
        Already paid? Get your PDF
      </p>
      <p className="text-xs text-pb-soft text-center mb-4">
        Enter the email you used at checkout — we'll re-verify with Stripe and re-send your file.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="input-recovery-email"
          className="flex-1 rounded-full border-2 border-pb-sky/40 bg-pb-cream px-5 py-3 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition"
        />
        <button
          data-testid="btn-recovery-submit"
          onClick={() => {
            if (sessionId) {
              recover({ session_id: sessionId });
            } else if (email.trim()) {
              recover({ email: email.trim() });
            } else {
              toast.error("Please enter the email you paid with.");
            }
          }}
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-pb-ink text-white font-extrabold px-6 py-3 hover:bg-pb-coral transition disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {submitting ? "Working…" : "Re-send my PDF"}
        </button>
      </div>
      <p className="text-[11px] text-pb-soft mt-3 text-center">
        Still stuck? Email <a href="mailto:hello@platebuddies.com" className="underline font-bold">hello@platebuddies.com</a> with your receipt and we'll fix it manually.
      </p>
    </div>
  );
}
